import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { PencilSimple } from '@phosphor-icons/react';
import { RemittanceLineItemsTable } from './remittance-line-items-table';
import { RemittanceVerificationPanel } from './remittance-verification-panel';
import { LinkEmailButton } from './link-email-button';
import { RemittanceFormDialog } from './RemittanceFormDialog';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  useOverrideRemittanceCustomer,
  useDeleteRemittanceLineItem,
} from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { useToast } from 'src/context/ToastContext';
import { Currency } from '@/components/common/Currency';
import type { RemittanceDetail } from '@erp/rpc';
import { T, useGT } from 'gt-react';

interface RemittanceDetailsProps {
  remittance: RemittanceDetail;
}

function CustomerAttributionRow({ remittance }: RemittanceDetailsProps) {
  const gt = useGT();
  const [isEditing, setIsEditing] = useState(false);
  const override = useOverrideRemittanceCustomer();

  const handleSelect = (customerId: string | undefined) => {
    if (!customerId) return;
    override.mutate(
      { remittanceAdviceId: remittance.id, customerId },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  return (
    <div className="col-span-2 space-y-0.5">
      <div className="text-xs font-medium text-muted-foreground">
        <T>Customer</T>
      </div>
      {isEditing ? (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <CustomersAutocomplete
              selectedCustomerId={remittance.matchedCustomer?.id}
              onCustomerSelect={handleSelect}
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={override.isPending}
            onClick={() => setIsEditing(false)}
          >
            <T context="cancel customer override on remittance">Cancel</T>
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {remittance.matchedCustomer ? (
            <Link
              to="/accounts-receivable/collections/$customerId"
              params={{ customerId: remittance.matchedCustomer.id }}
              viewTransition
              className="font-medium text-blue-600 hover:underline"
            >
              {remittance.matchedCustomer.name}
            </Link>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              <T context="remittance customer not yet attributed">Unmatched</T>
            </Badge>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs"
            disabled={override.isPending}
            onClick={() => setIsEditing(true)}
          >
            {remittance.matchedCustomer
              ? gt('Override', {
                  context: 'override customer attributed to remittance',
                })
              : gt('Assign customer', {
                  context: 'assign customer to remittance with no match',
                })}
          </Button>
        </div>
      )}
    </div>
  );
}

function RemittanceMetadataPanel({ remittance }: RemittanceDetailsProps) {
  const gt = useGT();
  const { showToast } = useToast();
  // Remittances are editable on every channel: ingested ones (email/EDI/portal)
  // are never re-synced, so corrections to extraction errors stick.
  const [editOpen, setEditOpen] = useState(false);
  const deleteLineItem = useDeleteRemittanceLineItem();

  async function handleDeleteLineItem(lineItemId: string) {
    try {
      await deleteLineItem.mutateAsync({ lineItemId });
      showToast({
        title: gt('Line item removed', {
          context: 'toast title, remittance line item removed',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      showToast({
        title: gt('Remove failed', {
          context: 'toast title, remittance line item remove failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">
              {remittance.statedCustomerName?.trim() ||
                gt('Unknown Customer', {
                  context:
                    'remittance metadata panel title fallback when no customer name',
                })}
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditOpen(true)}
            >
              <PencilSimple className="mr-1 size-4" />
              <T context="edit remittance">Edit</T>
            </Button>
          </div>
          <div className="mt-1">
            <div className="text-xs font-medium text-muted-foreground">
              <T>Total Amount</T>
            </div>
            <div className="text-2xl font-bold text-green-600">
              <Currency value={remittance.statedTotalAmount} fallback="--" />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <CustomerAttributionRow remittance={remittance} />
          <div className="my-3 border-t" />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                <T>Received Date</T>
              </div>
              <div className="font-medium">
                {remittance.receivedAt
                  ? new Date(remittance.receivedAt).toLocaleDateString()
                  : '--'}
              </div>
            </div>

            {remittance.statedPaymentDate && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Payment Date</T>
                </div>
                <div className="font-medium">
                  {formatDateOnlyString(remittance.statedPaymentDate)}
                </div>
              </div>
            )}

            {remittance.statedPaymentMethod && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Payment Method</T>
                </div>
                <div>
                  {remittance.statedPaymentMethod
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                </div>
              </div>
            )}

            {remittance.statedReferenceNo && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Reference</T>
                </div>
                <div>{remittance.statedReferenceNo}</div>
              </div>
            )}

            {remittance.senderEmail && (
              <div className="col-span-2 space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Sender</T>
                </div>
                <div>{remittance.senderEmail}</div>
              </div>
            )}

            {remittance.linkedPaymentId && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Linked Payment</T>
                </div>
                <div>
                  <Link
                    to="/accounts-receivable/payments/$arPaymentId"
                    params={{ arPaymentId: remittance.linkedPaymentId }}
                    viewTransition
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {remittance.linkedPaymentRef ??
                      gt('View payment', {
                        context: 'linked payment link fallback on remittance',
                      })}
                  </Link>
                </div>
              </div>
            )}

            {remittance.linkedPaymentId && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Bank Status</T>
                </div>
                {remittance.bankCleared ? (
                  <Badge
                    variant="outline"
                    className="border-green-200 bg-green-50 text-green-800"
                  >
                    <T context="ar remittance bank cleared">Bank cleared</T>
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="border-amber-200 bg-amber-50 text-amber-800"
                  >
                    <T context="ar remittance awaiting bank">Awaiting bank</T>
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <RemittanceVerificationPanel
        verificationResult={remittance.verificationResult}
      />

      <Separator />
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-semibold">
            <T context="ar remittance">Line Items</T>
          </div>
          <div className="text-xs text-muted-foreground">
            <T context="hint, remittance line item negative amount means deduction">
              Tip: enter a negative amount for a deduction.
            </T>
          </div>
        </div>
        <RemittanceLineItemsTable
          lineItems={remittance.lineItems}
          editable
          remittanceAdviceId={remittance.id}
          customerId={remittance.matchedCustomer?.id}
          onDelete={handleDeleteLineItem}
          deletingId={
            deleteLineItem.isPending
              ? deleteLineItem.variables?.lineItemId
              : null
          }
        />
      </div>

      <RemittanceFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        remittance={remittance}
      />
    </div>
  );
}

export function RemittanceDetails({ remittance }: RemittanceDetailsProps) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'ar-remittance-details-panel-size',
    {
      defaultValue: 50,
      initializeWithValue: true,
    },
  );

  const sourceEmail = remittance.sourceEmail;

  if (!sourceEmail) {
    return (
      <div className="flex h-full flex-col px-3 py-3">
        <RemittanceMetadataPanel remittance={remittance} />
        <div className="mt-4 flex justify-center">
          <LinkEmailButton remittanceAdviceId={remittance.id} />
        </div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <RemittanceMetadataPanel remittance={remittance} />
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        <SmartEmailViewer
          emailData={inboundEmailToEmailData(sourceEmail)}
          conversationId={sourceEmail.conversationId}
          defaultTab="email"
          sourceEmailId={sourceEmail.id}
          highlightedEmailIds={[sourceEmail.id]}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
